import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { ScreenLoader } from 'src/components/screenLoader';
import { DateInput } from "@mantine/dates";
import { formatDateddmmyyyy } from 'src/utils';
import { stepAndRouteMap } from "src/constants";
import useCleverTapEvents from 'src/events/useGetEvents';
import { PAN_EVENT } from 'src/events/constant';
import { ScreenWrapper } from '../../components';
import { OnboardingProgress } from '../../components/onboardingProgress';
import { RootState } from '../../redux/store';
import { useStyles } from './panCardVerify.styles';
import {
  clearPanDetailsFormError,
  setPanDetailsFormStatus,
  verifyPanNumber,
} from "./panDetailsFormSlice";
import { Button, TextInput } from '../../components/atoms';

const regexPANCheck = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

export const PanCardVerify = (): JSX.Element => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const panDetailsState = useSelector((state: RootState) => state.panDetails);
  const accountState = useSelector((state: RootState) => state.account);
  const [panNumberVal, setPanNumberVal] = useState("");
  const [dob, setDob] = useState<any>(null);
  const isButtonDisabled = !(panNumberVal.length >= 10 && dob !== null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { userDetails } = accountState;
  const { TriggerEvent } = useCleverTapEvents();

  useEffect(() => {
    if (accountState?.userDetails?.pan) {
      setPanNumberVal(accountState?.userDetails?.pan || "");
    }
  }, [accountState?.userDetails?.pan]);

  const onPanNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const alphaNumericRegex = /^[A-Z0-9]*$/;
    const panNumber = e.target.value.toUpperCase();
    if (alphaNumericRegex.test(panNumber)) {
      setPanNumberVal(panNumber);
      if (panNumber.length === 10 && !regexPANCheck.test(panNumber)) {
        setErrorMessage("Enter valid PAN Number");
      } else {
        setErrorMessage("");
      }
    }
  };

  const isValidPan = (value: string) => {
    const regex = /([A-Z]){5}([0-9]){4}([A-Z]){1}$/;
    if (regex.test(value.toUpperCase())) {
      return true;
    }
    return false;
  };

  // On pan confirmation api call
  useEffect(() => {
    if (panDetailsState.status === "success") {
      dispatch(setPanDetailsFormStatus("idle"));
      notifications.show({
        color: "green",
        message: "PAN card Verified Successfully!",
        icon: <IconSuccess />,
      });
      const nextStepFromBackend = userDetails.nextStep;
      navigate(stepAndRouteMap[nextStepFromBackend][0]);
    } else if (panDetailsState.status === "error") {
      dispatch(clearPanDetailsFormError());
      notifications.show({
        color: "red",
        message: panDetailsState.error.message || "PAN card verification failed!",
        icon: <IconError />,
      });
    }
  }, [panDetailsState.status]);

  const onNextClick = () => {
    dispatch(
      verifyPanNumber({
        pan: panNumberVal,
        dateOfBirth: formatDateddmmyyyy(dob),
      })
    );
    TriggerEvent(PAN_EVENT.panSubmit, {
      yearOfBirth: new Date(dob).getFullYear().toString(),
    })
  };
  const handleKeyPress = (event: any) => {
    if (event.key === "Enter" && !isButtonDisabled) {
      onNextClick();
    }
  };

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={8.25} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <div>
            <h2 className="csHeading">PAN</h2>
            <p className="csDescription">
              Please enter your PAN Number and DOB
            </p>
            <TextInput
              type="text"
              maxLength={10}
              name="PAN"
              id="pan-number-input"
              title="PAN"
              autoComplete="off"
              placeholder="Your 10 digit PAN Number"
              onChange={onPanNumberChange}
              onKeyDown={handleKeyPress}
              value={panNumberVal}
              mb={16}
              error={errorMessage}
              autoFocus
            />
            <DateInput
              value={dob}
              onChange={setDob}
              placeholder="Date of birth (in DD/MM/YYYY)"
              mx="auto"
              valueFormat="DD/MM/YYYY"
              classNames={{
                input: classes.inputDob,
              }}
            />
            {/* <ChangeDob /> */}
          </div>
          <p className={classes.tAndCText}>
            By Clicking on Next, you are allowing Butterfly Broking Private
            Limited to access and update your data in KRA Registry.
          </p>
        </div>
        <div className="csFooter">
          <Button
            disabled={
              isButtonDisabled || !isValidPan(panNumberVal) || errorMessage
            }
            onClick={onNextClick}
          >
            Next
          </Button>
        </div>
      </div>
      {panDetailsState.status === "loading" ? <ScreenLoader /> : null}
    </ScreenWrapper>
  );
};

export default PanCardVerify;
